'use client'
import { useEffect, useRef, useState } from 'react'

import { Textarea } from './ui/textarea'

interface UseAutosizeTextAreaProps {
  maxHeight?: number
  minHeight?: number
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  triggerAutoSize: string
}

// NOTE: AutosizeTextarea is a component by Spectrum UI

export const useAutosizeTextArea = ({
  maxHeight = Number.MAX_SAFE_INTEGER,
  minHeight = 0,
  textAreaRef,
  triggerAutoSize,
}: UseAutosizeTextAreaProps) => {
  useEffect(() => {
    const textArea = textAreaRef.current
    if (!textArea) return
    // We need to reset the height momentarily to get the correct scrollHeight for the textarea
    const offsetBorder = 2
    textArea.style.minHeight = `${minHeight + offsetBorder}px`
    textArea.style.maxHeight = `${maxHeight}px`
    // Reset to auto to get accurate scrollHeight measurement
    textArea.style.height = 'auto'
    const scrollHeight = textArea.scrollHeight
    // We then set the height directly, outside of the render loop
    // Trying to set this with state or a ref will product an incorrect value.
    if (scrollHeight > maxHeight) {
      textArea.style.height = `${maxHeight}px`
    } else {
      textArea.style.height = `${Math.max(scrollHeight, minHeight + offsetBorder)}px`
    }
  }, [textAreaRef, triggerAutoSize, minHeight, maxHeight])
}

export type AutosizeTextAreaRef = {
  focus: () => void
  maxHeight: number
  minHeight: number
  textArea: HTMLTextAreaElement | null
}

type AutosizeTextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  maxHeight?: number
  minHeight?: number
  ref?: React.Ref<AutosizeTextAreaRef>
  variant?: React.ComponentProps<typeof Textarea>['variant']
}

export function AutosizeTextarea({
  maxHeight = Number.MAX_SAFE_INTEGER,
  minHeight = 0,
  onChange,
  ref,
  value,
  ...props
}: AutosizeTextAreaProps) {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const [typedValue, setTypedValue] = useState('')

  useAutosizeTextArea({
    maxHeight,
    minHeight,
    textAreaRef,
    triggerAutoSize: value === undefined ? typedValue : String(value),
  })

  useEffect(() => {
    if (!ref) return

    const refValue = {
      focus: () => textAreaRef?.current?.focus(),
      maxHeight,
      minHeight,
      textArea: textAreaRef.current,
    }

    if (typeof ref === 'function') {
      ref(refValue)
    } else {
      ref.current = refValue
    }
  }, [ref, maxHeight, minHeight])

  return (
    <Textarea
      {...props}
      onChange={e => {
        setTypedValue(e.target.value)
        onChange?.(e)
      }}
      ref={textAreaRef}
      value={value}
    data-sentry-element="Textarea" data-sentry-component="AutosizeTextarea" data-sentry-source-file="autosize-textarea.tsx" />
  )
}
